import { Button } from '@crowdex/ui/components/button';
import { Input } from '@crowdex/ui/components/input';
import {
  ArrowLeft,
  ArrowRight,
  Eye,
  Globe,
  Keyboard,
  LoaderCircle,
  MousePointer2,
  RotateCw,
} from 'lucide-react';
import {
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { EDGE_WIDTH } from '@/components/mobile-shell/use-swipe-back';
import { resolveBrowserInput } from '@/lib/browser-context';
import { cloudViewUrl } from '@/lib/cloud-runtime/cloud-display';
import type { CloudRuntime } from '@/lib/cloud-runtime/cloud-runtime';
import { type Keystroke, typedKeystrokes } from '@/lib/cloud-runtime/typed-keystrokes';

type Tab = { id: string; title: string; url: string; active: boolean };
type Frame = { tabId: string; data: string; width: number; height: number };
type ServerMessage =
  | { type: 'tabs'; tabs: Tab[] }
  | { type: 'watching'; tabId: string | null }
  | ({ type: 'frame' } & Frame)
  | { type: 'error'; message: string };

const RETRY_DELAY_MS = 3_000;
/** The largest frame asked for, in device pixels: enough for a maximized panel. */
const MAX_FRAME_PIXELS = 1920;

/** CDP's modifier bits: Alt 1, Ctrl 2, Meta 4, Shift 8. */
function modifiers(event: {
  altKey: boolean;
  ctrlKey: boolean;
  metaKey: boolean;
  shiftKey: boolean;
}) {
  return (
    (event.altKey ? 1 : 0) |
    (event.ctrlKey ? 2 : 0) |
    (event.metaKey ? 4 : 0) |
    (event.shiftKey ? 8 : 0)
  );
}

const BUTTONS = ['left', 'middle', 'right'] as const;

/** The keys a touch keyboard's field types itself (read from its changes), or composes with. */
const TEXT_KEYS = new Set(['Enter', 'Backspace', 'Delete', 'Unidentified', 'Process', 'Dead']);

/**
 * The cloud sandbox's browser, live and on its own: the tabs the agent opened, streamed from
 * Chromium. Watched by default; the user takes control to click, scroll and type (a login, a
 * captcha), or opens an address. Connected only while shown.
 */
export function CloudBrowserView({
  cloudRuntime,
  visible,
}: {
  cloudRuntime: CloudRuntime;
  visible: boolean;
}) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const socket = useRef<WebSocket | null>(null);
  const frame = useRef<Frame | null>(null);
  /** Where the last frame was drawn in the canvas, in CSS pixels. */
  const drawn = useRef({ x: 0, y: 0, width: 0, height: 0 });
  const [connected, setConnected] = useState(false);
  const [tabs, setTabs] = useState<Tab[]>([]);
  const [watching, setWatching] = useState<string | null>(null);
  const [chosen, setChosen] = useState<string | null>(null);
  const [hasFrame, setHasFrame] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [address, setAddress] = useState('');
  const [editingAddress, setEditingAddress] = useState(false);
  const [control, setControl] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const chosenRef = useRef(chosen);
  chosenRef.current = chosen;

  const send = useCallback((message: unknown) => {
    const current = socket.current;
    if (current?.readyState === WebSocket.OPEN) current.send(JSON.stringify(message));
  }, []);

  const watch = useCallback(
    (tabId: string | null) => {
      const element = canvas.current;
      const scale = window.devicePixelRatio || 1;
      const width = Math.min(MAX_FRAME_PIXELS, (element?.clientWidth || 1280) * scale);
      const height = Math.min(MAX_FRAME_PIXELS, (element?.clientHeight || 720) * scale);
      send({ type: 'watch', tabId, maxWidth: width, maxHeight: height });
    },
    [send],
  );

  const draw = useCallback(() => {
    const element = canvas.current;
    const current = frame.current;
    if (element === null || current === null) return;
    const image = new Image();
    image.onload = () => {
      const scale = window.devicePixelRatio || 1;
      const width = element.clientWidth;
      const height = element.clientHeight;
      if (element.width !== width * scale || element.height !== height * scale) {
        element.width = width * scale;
        element.height = height * scale;
      }
      const context = element.getContext('2d');
      if (context === null) return;
      // The page keeps its proportions, centered in the panel.
      const fit = Math.min(width / current.width, height / current.height);
      const box = {
        width: current.width * fit,
        height: current.height * fit,
        x: (width - current.width * fit) / 2,
        y: (height - current.height * fit) / 2,
      };
      drawn.current = box;
      context.setTransform(scale, 0, 0, scale, 0, 0);
      context.clearRect(0, 0, width, height);
      context.drawImage(image, box.x, box.y, box.width, box.height);
    };
    image.src = `data:image/jpeg;base64,${current.data}`;
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies(attempt): the attempt counter reconnects after the socket dropped.
  useEffect(() => {
    if (!visible) return;
    let disposed = false;
    let retry: ReturnType<typeof setTimeout> | undefined;
    const opened = new WebSocket(cloudViewUrl(cloudRuntime, 'browser'));
    socket.current = opened;
    opened.addEventListener('open', () => {
      opened.send(JSON.stringify({ type: 'hello', token: cloudRuntime.token }));
      setConnected(true);
      setError(null);
      watch(chosenRef.current);
    });
    opened.addEventListener('message', (event) => {
      if (typeof event.data !== 'string') return;
      const message = JSON.parse(event.data) as ServerMessage;
      if (message.type === 'tabs') setTabs(message.tabs);
      else if (message.type === 'watching') setWatching(message.tabId);
      else if (message.type === 'error') setError(message.message);
      else if (message.type === 'frame') {
        frame.current = message;
        setHasFrame(true);
        draw();
      }
    });
    opened.addEventListener('close', (event) => {
      if (socket.current === opened) socket.current = null;
      if (disposed) return;
      setConnected(false);
      if (event.reason) setError(event.reason);
      // The sandbox may be restarting: try again while the view is shown.
      retry = setTimeout(() => setAttempt((value) => value + 1), RETRY_DELAY_MS);
    });
    return () => {
      disposed = true;
      clearTimeout(retry);
      opened.close();
    };
  }, [cloudRuntime, visible, attempt, watch, draw]);

  // A resized panel asks for frames of its new size.
  useEffect(() => {
    const element = canvas.current;
    if (element === null || !connected) return;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const observer = new ResizeObserver(() => {
      draw();
      clearTimeout(timer);
      timer = setTimeout(() => watch(chosenRef.current), 250);
    });
    observer.observe(element);
    return () => {
      clearTimeout(timer);
      observer.disconnect();
    };
  }, [connected, draw, watch]);

  const current = tabs.find((tab) => tab.id === watching) ?? null;
  useEffect(() => {
    if (!editingAddress) setAddress(current?.url === 'about:blank' ? '' : (current?.url ?? ''));
  }, [current?.url, editingAddress]);
  useEffect(() => {
    if (current === null) {
      frame.current = null;
      setHasFrame(false);
    }
  }, [current]);

  /** The page's CSS pixel under the pointer, or null outside the drawn page. */
  const pagePoint = (event: { clientX: number; clientY: number }) => {
    const element = canvas.current;
    const shown = frame.current;
    if (element === null || shown === null || drawn.current.width === 0) return null;
    const bounds = element.getBoundingClientRect();
    const x = ((event.clientX - bounds.left - drawn.current.x) / drawn.current.width) * shown.width;
    const y =
      ((event.clientY - bounds.top - drawn.current.y) / drawn.current.height) * shown.height;
    return x < 0 || y < 0 || x > shown.width || y > shown.height ? null : { x, y };
  };

  const mouse = (
    event: ReactMouseEvent<HTMLCanvasElement>,
    action: 'mousePressed' | 'mouseReleased' | 'mouseMoved',
  ) => {
    if (!control || watching === null) return;
    const point = pagePoint(event);
    if (point === null) return;
    if (action === 'mousePressed') event.currentTarget.focus();
    send({
      type: 'input',
      tabId: watching,
      event: {
        type: 'mouse',
        action,
        ...point,
        button: action === 'mouseMoved' ? 'none' : (BUTTONS[event.button] ?? 'left'),
        clickCount: action === 'mouseMoved' ? 0 : Math.min(3, Math.max(1, event.detail)),
        modifiers: modifiers(event),
      },
    });
  };

  const key = (event: ReactKeyboardEvent<HTMLElement>, action: 'keyDown' | 'keyUp') => {
    if (!control || watching === null) return;
    event.preventDefault();
    const text =
      event.key.length === 1 && !event.ctrlKey && !event.metaKey
        ? event.key
        : event.key === 'Enter'
          ? '\r'
          : undefined;
    send({
      type: 'input',
      tabId: watching,
      event: {
        type: 'key',
        action,
        key: event.key,
        code: event.code,
        keyCode: event.keyCode,
        modifiers: modifiers(event),
        ...(text === undefined ? {} : { text }),
      },
    });
  };

  // A touch screen types through a field of its own: focusing it raises the phone's keyboard,
  // and what changes in it is typed into the page.
  const typingField = useRef<HTMLTextAreaElement>(null);
  const typed = useRef('');
  const backspaceForwarded = useRef(false);
  const [typing, setTyping] = useState(false);
  const stroke = (keystroke: Keystroke) => {
    if (watching === null) return;
    for (const action of ['keyDown', 'keyUp'] as const)
      send({
        type: 'input',
        tabId: watching,
        event: { type: 'key', action, ...keystroke, modifiers: 0 },
      });
  };
  const toggleKeyboard = () => {
    const field = typingField.current;
    if (field === null) return;
    if (document.activeElement === field) field.blur();
    else field.focus();
  };
  useEffect(() => {
    if (!control) typingField.current?.blur();
  }, [control]);

  // Wheel events are passive in React: listened to directly, to keep the panel from scrolling.
  useEffect(() => {
    const element = canvas.current;
    if (element === null || !control || watching === null) return;
    const wheel = (event: WheelEvent) => {
      const point = pagePoint(event);
      if (point === null) return;
      event.preventDefault();
      send({
        type: 'input',
        tabId: watching,
        event: {
          type: 'wheel',
          ...point,
          deltaX: event.deltaX,
          deltaY: event.deltaY,
          modifiers: modifiers(event),
        },
      });
    };
    element.addEventListener('wheel', wheel, { passive: false });
    return () => element.removeEventListener('wheel', wheel);
  });

  // A touch screen has no wheel: a finger dragged over the page scrolls it (a tap clicks through
  // the mouse events the browser emulates). The left edge stays the phone's swipe back.
  useEffect(() => {
    const element = canvas.current;
    if (element === null || !control || watching === null) return;
    let last: { x: number; y: number } | null = null;
    const start = (event: TouchEvent) => {
      const touch = event.touches[0];
      last =
        event.touches.length === 1 && touch !== undefined && touch.clientX > EDGE_WIDTH
          ? { x: touch.clientX, y: touch.clientY }
          : null;
    };
    const move = (event: TouchEvent) => {
      const touch = event.touches[0];
      const shown = frame.current;
      if (last === null || touch === undefined || shown === null || drawn.current.width === 0)
        return;
      const point = pagePoint(touch);
      if (point === null) return;
      event.preventDefault();
      const scale = shown.width / drawn.current.width;
      send({
        type: 'input',
        tabId: watching,
        event: {
          type: 'wheel',
          ...point,
          deltaX: (last.x - touch.clientX) * scale,
          deltaY: (last.y - touch.clientY) * scale,
          modifiers: 0,
        },
      });
      last = { x: touch.clientX, y: touch.clientY };
    };
    const end = () => {
      last = null;
    };
    element.addEventListener('touchstart', start, { passive: true });
    element.addEventListener('touchmove', move, { passive: false });
    element.addEventListener('touchend', end);
    element.addEventListener('touchcancel', end);
    return () => {
      element.removeEventListener('touchstart', start);
      element.removeEventListener('touchmove', move);
      element.removeEventListener('touchend', end);
      element.removeEventListener('touchcancel', end);
    };
  });

  const select = (tabId: string) => {
    setChosen(tabId);
    watch(tabId);
  };

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex h-11 shrink-0 items-center gap-1 border-hairline-strong border-b px-2 text-xs">
        <Button
          size="sm"
          variant="ghost"
          aria-label="Back"
          disabled={current === null}
          onClick={() => send({ type: 'history', tabId: watching, action: 'back' })}
        >
          <ArrowLeft />
        </Button>
        <Button
          size="sm"
          variant="ghost"
          aria-label="Forward"
          disabled={current === null}
          onClick={() => send({ type: 'history', tabId: watching, action: 'forward' })}
        >
          <ArrowRight />
        </Button>
        <Button
          size="sm"
          variant="ghost"
          aria-label="Reload"
          disabled={current === null}
          onClick={() => send({ type: 'history', tabId: watching, action: 'reload' })}
        >
          <RotateCw />
        </Button>
        <form
          className="min-w-0 flex-1"
          onSubmit={(event) => {
            event.preventDefault();
            const url = resolveBrowserInput(address);
            if (url) send({ type: 'navigate', tabId: watching, url });
            setEditingAddress(false);
            (document.activeElement as HTMLElement | null)?.blur();
          }}
        >
          <Input
            aria-label="Address"
            className="h-control-sm text-xs"
            disabled={!connected}
            placeholder="Open an address in the cloud browser"
            value={address}
            onFocus={() => setEditingAddress(true)}
            onBlur={() => setEditingAddress(false)}
            onChange={(event) => setAddress(event.target.value)}
          />
        </form>
        {control && (
          <Button
            aria-label={typing ? 'Hide the keyboard' : 'Type in the page'}
            aria-pressed={typing}
            className="pointer-fine:hidden"
            onClick={toggleKeyboard}
            // Keeps the field focused: a press elsewhere would blur it first.
            onMouseDown={(event) => event.preventDefault()}
            size="sm"
            variant={typing ? 'secondary' : 'ghost'}
          >
            <Keyboard />
          </Button>
        )}
        <Button
          aria-label={control ? 'Release control' : 'Take control'}
          size="sm"
          variant={control ? 'secondary' : 'ghost'}
          disabled={!connected || current === null}
          onClick={() => setControl((value) => !value)}
        >
          {control ? <Eye /> : <MousePointer2 />}
          {/* A phone leaves the room to the address. */}
          <span className="max-sm:hidden">{control ? 'Release control' : 'Take control'}</span>
        </Button>
      </div>
      {tabs.length > 1 && (
        <div className="flex shrink-0 gap-1 overflow-x-auto border-hairline-strong border-b px-2 py-1">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              title={tab.url}
              onClick={() => select(tab.id)}
              className={`type-ui max-w-48 shrink-0 truncate rounded-md px-2 py-1 text-xs ${
                tab.id === watching
                  ? 'bg-accent text-foreground'
                  : 'text-muted-foreground hover:bg-accent/70'
              }`}
            >
              {tab.title || tab.url || 'New tab'}
            </button>
          ))}
        </div>
      )}
      <div className="relative min-h-0 flex-1 bg-muted/40">
        <canvas
          ref={canvas}
          data-testid="cloud-browser-screen"
          tabIndex={control ? 0 : -1}
          className={`absolute inset-0 h-full w-full outline-none ${control ? 'cursor-default touch-none' : 'cursor-not-allowed'}`}
          onMouseDown={(event) => mouse(event, 'mousePressed')}
          onMouseUp={(event) => mouse(event, 'mouseReleased')}
          onMouseMove={(event) => {
            if (event.buttons !== 0 || control) mouse(event, 'mouseMoved');
          }}
          onContextMenu={(event) => {
            if (control) event.preventDefault();
          }}
          onKeyDown={(event) => key(event, 'keyDown')}
          onKeyUp={(event) => key(event, 'keyUp')}
        />
        <textarea
          aria-label="Text typed into the page"
          autoCapitalize="off"
          autoComplete="off"
          className="pointer-events-none absolute bottom-0 left-0 size-px resize-none text-base opacity-0"
          onBlur={(event) => {
            event.currentTarget.value = '';
            typed.current = '';
            setTyping(false);
          }}
          onFocus={() => setTyping(true)}
          onInput={(event) => {
            const value = event.currentTarget.value;
            for (const keystroke of typedKeystrokes(typed.current, value)) stroke(keystroke);
            typed.current = value;
            // A word at a time is enough to follow: what was typed long ago is let go.
            if (value.length > 200) {
              event.currentTarget.value = '';
              typed.current = '';
            }
          }}
          // The keys that change no text (arrows, Tab, Escape) are sent as they are.
          onKeyDown={(event) => {
            // An empty field changes nothing on a backspace: the page's own text is erased.
            backspaceForwarded.current =
              event.key === 'Backspace' && event.currentTarget.value === '';
            if (backspaceForwarded.current || (event.key.length > 1 && !TEXT_KEYS.has(event.key)))
              key(event, 'keyDown');
          }}
          onKeyUp={(event) => {
            const forwarded = event.key === 'Backspace' && backspaceForwarded.current;
            if (forwarded) backspaceForwarded.current = false;
            if (forwarded || (event.key.length > 1 && !TEXT_KEYS.has(event.key)))
              key(event, 'keyUp');
          }}
          ref={typingField}
          spellCheck={false}
          tabIndex={-1}
        />
        {!hasFrame && (
          <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center gap-2 p-6 text-center text-muted-foreground text-sm">
            {!connected || (current !== null && !hasFrame) ? (
              <LoaderCircle className="size-5 animate-spin" />
            ) : (
              <Globe className="size-5" />
            )}
            <span>
              {!connected
                ? 'Connecting to the cloud browser…'
                : current !== null
                  ? 'Loading the page…'
                  : 'The agent has not opened a page yet. Open an address above to start one.'}
            </span>
            {error !== null && <span className="text-xs">{error}</span>}
          </div>
        )}
      </div>
    </div>
  );
}
